import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Clock, Download, FileX2, Info, Loader2, Mail, PenLine, Phone, ThumbsDown, XCircle } from "lucide-react";
import nelgatesLogo from "@/icons/nelgates-logo-horizontal.png";
import { AgreementDocument } from "@/components/agreements/AgreementDocument";
import { SigningDialog, type SigningValues } from "@/components/agreements/SigningDialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { dateLocale, withClock } from "@/lib/dateTime";
import type { PublicAgreement } from "@/types/agreement";

const fmtDateTime = (d: string) => new Date(d).toLocaleString(dateLocale(), withClock({ day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }));

async function call(url: string, method: "GET" | "POST", body?: unknown) {
  const r = await fetch(url, { method, credentials: "include", headers: { Accept: "application/json", ...(body ? { "Content-Type": "application/json" } : {}) }, body: body ? JSON.stringify(body) : undefined });
  const data = await r.json().catch(() => null);
  if (!r.ok) throw new Error(String(data?.Message || data?.message || "Something went wrong. Please try again.").split("\n")[0]);
  return data;
}

/**
 * A signer's view of an agreement (/a/<secret>). No login: the link is the key. Plain fetch is used (not the CRM api
 * helper) so a signed-out visitor never triggers the CRM's session handling.
 */
export function PublicAgreementPage() {
  const { token = "" } = useParams<{ token: string }>();
  const base = `/api/Public/Agreement/${encodeURIComponent(token)}`;
  const [data, setData] = useState<PublicAgreement | null>(null);
  const [problem, setProblem] = useState("");
  const [signOpen, setSignOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [declineProblem, setDeclineProblem] = useState("");

  // Never indexed, and the secret link is never sent as a referrer; always light, whatever theme a signed-in rep uses
  useEffect(() => {
    const metas = [
      ["robots", "noindex, nofollow"],
      ["referrer", "no-referrer"],
    ].map(([name, content]) => {
      const m = document.createElement("meta");
      m.name = name!;
      m.content = content!;
      document.head.appendChild(m);
      return m;
    });
    const wasDark = document.documentElement.classList.contains("dark");
    document.documentElement.classList.remove("dark");
    return () => {
      metas.forEach((m) => m.remove());
      if (wasDark) document.documentElement.classList.add("dark");
    };
  }, []);

  const load = () =>
    call(base, "GET")
      .then((body) => {
        setData(body as PublicAgreement);
        document.title = body?.document?.title || body?.title || "Agreement";
      })
      .catch((err) => setProblem(err.message || "This link isn't valid."));

  useEffect(() => {
    load();
  }, [token]);

  const decline = async (e: React.FormEvent) => {
    e.preventDefault();
    setDeclineProblem("");
    if (!reason.trim()) return setDeclineProblem("Please tell us why, so we can put it right.");
    setBusy(true);
    try {
      await call(`${base}/decline`, "POST", { reason: reason.trim() });
      setDeclineOpen(false);
      await load();
    } catch (err: any) {
      setDeclineProblem(err.message);
    } finally {
      setBusy(false);
    }
  };

  const sign = async (v: SigningValues) => {
    await call(`${base}/sign`, "POST", v);
    setSignOpen(false);
    await load();
  };

  if (problem) {
    return (
      <Shell>
        <Notice icon={FileX2} title="This link can't be opened" body={problem} />
      </Shell>
    );
  }
  if (!data) {
    return (
      <Shell>
        <div className="py-24 flex justify-center">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      </Shell>
    );
  }

  const rep = data.contact_person;
  const doc = data.document;
  const open = data.state === "open" && !!doc;
  const done = data.state === "signed" || data.state === "executed";

  return (
    <Shell
      company={data.company.name}
      title={`Agreement with ${doc?.customer.company || data.title}`}
      subtitle={data.display_number}
      action={
        doc && (
          <>
            <a href={`${base}/pdf`} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-border bg-white px-3.5 text-[13px] font-medium text-foreground hover:bg-muted">
              <Download className="size-4" /> {data.state === "executed" && doc.source !== "UPLOADED" ? "Download signed PDF" : "Download PDF"}
            </a>
            {data.state === "executed" && doc.source === "UPLOADED" && (
              <a href={`${base}/certificate`} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-border bg-white px-3.5 text-[13px] font-medium text-foreground hover:bg-muted">
                <Download className="size-4" /> Certificate
              </a>
            )}
            {open && (
              <button type="button" onClick={() => setSignOpen(true)} className="inline-flex h-9 items-center rounded-lg bg-primary px-4 text-[13px] font-semibold text-white hover:bg-primary/90">
                Review and sign
              </button>
            )}
          </>
        )
      }
    >
      <div className="mx-auto flex w-full max-w-[1180px] flex-wrap items-start justify-center gap-6">
        <div className="min-w-0 max-w-3xl flex-[1_1_560px] space-y-4">
          {data.internal && (
            <div className="flex gap-2 rounded-xl border border-primary/20 bg-primary/10 px-4 py-2.5 text-xs text-primary">
              <Info className="size-4 shrink-0" /> You're signed in to the CRM, so this visit isn't counted as the signer's. You can't sign for them.
            </div>
          )}
          {data.state === "waiting" && <Banner tone="info" icon={Clock} title="Waiting for the other signers" body="Someone has to sign before you. We'll email you as soon as it's your turn. You can read the agreement meanwhile." />}
          {done && data.signer?.signed_at && (
            <Banner
              tone="success"
              icon={CheckCircle2}
              title={data.state === "executed" ? "Signed by everyone: the agreement is in force" : `Thank you, ${data.signer.name}. You signed on ${fmtDateTime(data.signer.signed_at)}`}
              body={data.state === "executed" ? "The signed copy, with its certificate, is attached to the email we sent, and you can download it above." : "The other signers are next. You'll get the fully signed copy by email once everyone has signed."}
            />
          )}
          {data.state === "declined" && <Banner tone="danger" icon={XCircle} title="This agreement was declined" body={data.decline_reason ? `Reason given: ${data.decline_reason}` : "It can't be signed any more."} />}
          {data.state === "terminated" && <Banner tone="danger" icon={XCircle} title="This agreement has ended" body="It can't be signed any more." />}
          {doc ? (
            doc.source === "UPLOADED" ? (
              <div className="overflow-hidden rounded-xl border bg-white">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2.5 text-sm">
                  <span className="truncate font-semibold">{doc.file?.name || "agreement.pdf"}</span>
                  <span className="text-xs text-muted-foreground">This is the exact document you sign</span>
                </div>
                <iframe title={doc.title} src={`${base}/pdf#toolbar=0`} className="block h-[75vh] w-full bg-muted/40" />
              </div>
            ) : (
              <div className="rounded-2xl bg-muted/50 p-0 sm:p-1">
                <AgreementDocument
                  doc={doc}
                  letterhead={data.letterhead ? { src: `${base}/letterhead`, top: data.letterhead.top, bottom: data.letterhead.bottom } : null}
                />
              </div>
            )
          ) : (
            <Notice icon={FileX2} title={data.state === "superseded" ? "This agreement was replaced" : "This agreement is no longer open"} body={data.state === "superseded" ? "A newer version was sent. Please use the link in the newer email." : "Please contact the sender for an updated one."} />
          )}
          {open && (
            <section aria-label="Sign this agreement" className="rounded-xl border-[1.5px] border-primary bg-accent/40 px-5 py-5">
              <h2 className="text-lg font-semibold text-foreground">Sign this agreement</h2>
              <p className="mt-0.5 text-[13px] text-muted-foreground">
                Signing as {data.signer?.name}. Your signature is recorded with the time, and everyone gets the signed copy once all have signed.
              </p>
              <div className="mt-4 flex flex-wrap gap-2.5">
                <button type="button" onClick={() => setSignOpen(true)} className="inline-flex h-11 items-center gap-1.5 rounded-[9px] bg-primary px-5 text-sm font-semibold text-white hover:bg-primary/90">
                  <PenLine className="size-4" /> Sign agreement
                </button>
                {rep?.email && (
                  <a href={`mailto:${rep.email}?subject=${encodeURIComponent(`Changes to agreement ${data.display_number}`)}`} className="inline-flex h-11 items-center rounded-[9px] border border-border bg-white px-4 text-sm font-medium text-foreground hover:bg-muted">
                    Request changes
                  </a>
                )}
                <button type="button" onClick={() => { setReason(""); setDeclineProblem(""); setDeclineOpen(true); }} className="h-11 px-3 text-sm text-danger hover:underline">
                  Decline
                </button>
              </div>
            </section>
          )}
        </div>

        {rep && (
          <aside className="w-full space-y-2 rounded-xl border bg-white p-4 text-sm text-foreground lg:sticky lg:top-20 lg:w-[240px]">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Your contact</div>
            <div className="font-semibold">{rep.name}</div>
            <div className="text-xs text-muted-foreground">{data.company.name}</div>
            <div className="flex flex-col gap-1 text-[13px]">
              {rep.phone && (
                <a href={`tel:${rep.phone}`} className="inline-flex items-center gap-1.5 text-primary hover:underline">
                  <Phone className="size-3.5" /> {rep.phone}
                </a>
              )}
              {rep.email && (
                <a href={`mailto:${rep.email}`} className="inline-flex items-center gap-1.5 break-all text-primary hover:underline">
                  <Mail className="size-3.5 shrink-0" /> {rep.email}
                </a>
              )}
            </div>
          </aside>
        )}
      </div>

      {open && data.signer && (
        <SigningDialog
          open={signOpen}
          onOpenChange={setSignOpen}
          heading={`Sign agreement ${data.display_number}`}
          description={`Sign below on behalf of ${doc?.customer.company || "your organisation"}.`}
          defaultName={data.signer.name}
          defaultTitle={data.signer.title || ""}
          statement={data.consent_text || ""}
          otp={data.otp_required ? { request: () => call(`${base}/otp`, "POST") } : undefined}
          submitLabel="Sign agreement"
          onSubmit={sign}
        />
      )}

      <Dialog open={declineOpen} onOpenChange={(o) => !busy && setDeclineOpen(o)}>
        <DialogContent className="sm:max-w-md bg-white text-foreground">
          <DialogHeader>
            <DialogTitle>Decline agreement {data.display_number}</DialogTitle>
            <DialogDescription className="text-muted-foreground">Declining ends this signing round for everyone. Tell us why so we can put it right.</DialogDescription>
          </DialogHeader>
          <form onSubmit={decline} className="space-y-3">
            <textarea className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40" rows={4} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. a clause needs changing, the price differs from what we agreed" />
            {declineProblem && <p className="text-sm text-danger">{declineProblem}</p>}
            <DialogFooter>
              <button type="button" disabled={busy} onClick={() => setDeclineOpen(false)} className="h-10 px-4 rounded-lg border border-border text-sm font-semibold text-foreground">
                Cancel
              </button>
              <button type="submit" disabled={busy} className="h-10 px-5 rounded-lg bg-sidebar text-white text-sm font-semibold hover:bg-sidebar/90 inline-flex items-center justify-center gap-1.5 disabled:opacity-70">
                {busy ? <Loader2 className="size-4 animate-spin" /> : <ThumbsDown className="size-4" />} Decline
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}

function Shell({ company, title, subtitle, action, children }: { company?: string; title?: string; subtitle?: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-muted text-foreground">
      <header className="sticky top-0 z-10 border-b bg-white">
        <div className="mx-auto flex min-h-14 max-w-[1180px] flex-wrap items-center gap-x-3.5 gap-y-2 px-4 py-2">
          <span className="flex min-w-[220px] flex-1 items-center gap-2.5">
            <img src={nelgatesLogo} alt={company || "Logo"} className="h-8 object-contain" />
            {title && (
              <span className="flex flex-col leading-tight">
                <span className="font-semibold">{title}</span>
                {subtitle && <span className="font-mono text-xs text-muted-foreground">{subtitle}</span>}
              </span>
            )}
          </span>
          <span className="flex flex-wrap items-center gap-2">{action}</span>
        </div>
      </header>
      <main className="px-3 py-6 sm:px-4">{children}</main>
    </div>
  );
}

function Notice({ icon: Icon, title, body }: { icon: React.ElementType; title: string; body: string }) {
  return (
    <div className="mx-auto max-w-xl rounded-xl border bg-white p-8 text-center">
      <Icon className="mx-auto size-10 text-muted-foreground" />
      <h1 className="mt-3 text-lg font-bold text-foreground">{title}</h1>
      <p className="mt-1.5 text-sm text-muted-foreground">{body}</p>
    </div>
  );
}

function Banner({ tone, icon: Icon, title, body }: { tone: "success" | "danger" | "info"; icon: React.ElementType; title: string; body: string }) {
  const cls = tone === "success" ? "border-success/30 bg-success-soft text-success" : tone === "danger" ? "border-danger/30 bg-danger-soft text-danger" : "border-primary/20 bg-primary/10 text-primary";
  return (
    <div role="status" className={`flex gap-2.5 rounded-xl border px-4 py-3 text-sm ${cls}`}>
      <Icon className="mt-0.5 size-5 shrink-0" />
      <div>
        <div className="font-semibold">{title}</div>
        <div className="text-[13px] opacity-90">{body}</div>
      </div>
    </div>
  );
}
